import React, { useState, useRef } from "react";
import LogoSvg from "/public/images/logos/lazyPrompts/sealTextHorizontal.svg";
import Button from "@/components/button";
import FloatingTextInput from "@/components/floating-text-input";
import Link from "next/link";
import css from "./index.module.scss";
import site from "/scripts/apis/site/index.js";
import Router from "next/router";
import { useRouter } from "next/router";
import {getCookie, setCookie} from "/scripts/utils/index.js";
import { useUser } from "scripts/apis/site/useUser";
import { sendDatalayerEvent } from "@/scripts/utils";

export default function SigninForm({ }) {
  const [cta, setCta] = useState("Sign In");
  const [message, setMessage] = useState("");
  const { query } = useRouter();
  const user = useUser({refreshInterval: 10000}, {
    populate: {
      listings: {
        fields: ["id", "name", "shortDescription", "price", "slug", "status", "business"],
        populate: {
          coverArt: true,
          aiModel: true,
        },
      },
      purchases: {
        fields: ["id", "name", "shortDescription", "price", "slug", "status", "business", "createdAt"],
        populate: {
          coverArt: true,
          fields: true,
          owner: true,
          aiModel: true,
        },
      },
    },
  });

  if (query.ref){
    setCookie("signinRef", query.ref);
  }

  const [form, setForm] = useState({
    email: useRef(),
    password: useRef(),
  });

  async function handleSubmit() {
    let allValid = true;
    for (const [key, element] of Object.entries(form)) {
      if (!element.current.isValid()) {
        allValid = false;
      }
    }

    if (allValid) {
      setCta("Signing In");
      site.query(site.endpoints.AUTH_SIGN_IN, {
        email: form.email.current.getValue().trim(),
        password: form.password.current.getValue().trim(),
      }).then((res) => {
        if (res.error) {
          if (res.error.status === 400) {
            setMessage("Invalid credentials");
            setCta("Sign In");
          } else {
            setStatus("error");
          }
        } else {
          user.mutate(res);
          sendDatalayerEvent("signin", "DL - Sign In", null);
          let ref = getCookie("signinRef") || "/account";
          if (ref.includes("auth")){
            ref = "/account";
          }
          Router.push(ref);
        }
      });
    }
  };



  return (
    <div className={`dark ${css.signInWrapper}`}>
      <div className={css.logo}>
        <LogoSvg />
      </div>
      <p>Sign in to buy and sell prompts.</p>
      {query.error && <p className={css.errorMessage}>{query.error}</p>}

      <FloatingTextInput
        className={css.input}
        name="email"
        label="Email"
        autocomplete="email"
        ref={form.email}
        validators={[
          { test: (val) => /^$/.test(val), class: "invalid", message: "Required" },
          { test: (val) => /^.*@.*$/.test(val), class: "valid", message: "" },
          { test: (val) => /.*/.test(val), class: "invalid", message: "Must be an email" },
        ]}
      />

      <FloatingTextInput
        name="password"
        className={css.input}
        label="Password"
        type="password"
        ref={form.password}
        validators={[
          { test: (val) => /^$/.test(val), class: "invalid", message: "Required" },
          { test: (val) => /^.{0,7}$/.test(val), class: "invalid", message: "Must be 8+ characters" },
          { test: (val) => /^(^([^0-9])*)$/.test(val), class: "invalid", message: "Must have a number" },
          { test: (val) => /^(^([^A-Z])*)$/.test(val), class: "invalid", message: "Must have an uppercase letter" },
          { test: (val) => /^(^([^a-z])*)$/.test(val), class: "invalid", message: "Must have a lowercase letter" },
          { test: (val) => /^..*$/.test(val), class: "valid", message: "Strength: high" },
        ]}
      />
      <Button raised className={css.button} label={cta} onClick={handleSubmit} />
      <div className={css.message}>{message}</div>
      <div className={css.links}>
        <Link href="/auth/register">Create account</Link>
        <Link href="/auth/forgot">Reset password</Link>
      </div>
    </div>
  );
}
